import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useInfiniteService from '@/data/hook/useInfiniteService';

import { activityQueries } from '@/data/query-keys/activityQueryKey';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import {
  createActivity,
  deleteActivity,
  updateActivityFromLesson,
} from '@/data/services/activityServices';
import Activity, { ActivityBlockType } from '@/models/Activity';
import {
  ActivityQuestionOrder,
  updateOrderQuestions,
} from '@/data/services/activityServices';
import { reorder } from '@/utils/list';
import compact from 'lodash/compact';
import { useEditableLesson } from '../lesson/useEditableLesson';

type UseActivitiesProps = {
  lessonId: number;
};

type CreateActivityData = {
  name: string;
  activityType: ActivityBlockType;
};

const useLessonActivities = ({ lessonId }: UseActivitiesProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab',
  });

  const {
    results: activities,
    isInitialLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    cacheManager,
  } = useInfiniteService<Activity>({
    ...activityQueries.listInfinite({
      lessonId,
      pageSize: ORDENABLE_PAGE_SIZE,
    }),
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const { runEditable } = useEditableLesson(lessonId);

  const createMutation = useMutation({
    mutationFn: (data: CreateActivityData) =>
      runEditable(editableLessonId =>
        createActivity(editableLessonId, {
          name: data.name,
          activityType: data.activityType,
        }),
      ),
    onSuccess: activity => {
      cacheManager.add(activity);
      alert.success(t('createBlockSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (activityId: number) =>
      runEditable(editableLessonId =>
        deleteActivity({ activityId, lessonId: editableLessonId }),
      ),
    onSuccess: (_, activityId) => {
      cacheManager.remove(activityId);
      alert.success(t('deleteBlockSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      activityId,
      data,
    }: {
      activityId: number;
      data: Partial<Activity>;
    }) =>
      runEditable(editableLessonId =>
        updateActivityFromLesson(
          { activityId, lessonId: editableLessonId },
          data,
        ),
      ),
    onSuccess: activity => {
      cacheManager.update(activity);
      alert.success(t('updateBlockSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const reorderMutation = useMutation({
    onMutate: (order: ActivityQuestionOrder[]) => {
      const allQuestions = activities.flatMap(a => a.questions ?? []);

      const reorderedActivities = compact(
        order.map((block, index) => {
          const activity = activities.find(a => a.id === block.id);
          if (!activity) return null;
          const questions = reorder(
            allQuestions,
            block.questions.map(q => q.identifier),
            'identifier',
          );
          return { ...activity, questions, order: index + 1 };
        }),
      );

      cacheManager.set(reorderedActivities);
    },
    mutationFn: (order: ActivityQuestionOrder[]) =>
      runEditable(editableLessonId =>
        updateOrderQuestions(editableLessonId, order),
      ),
    onSuccess: () => {
      alert.success(t('reorderQuestionsSuccess'));
    },
    onError: err => {
      cacheManager.undo();
      alert.error(getErrorMessage(err));
    },
  });

  return {
    activities,
    isLoading: isInitialLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    cacheManager,
    reorderActivities: reorderMutation.mutate,
    isReordering: reorderMutation.isPending,
    createActivity: createMutation.mutate,
    isCreating: createMutation.isPending,
    deleteActivity: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    updateActivity: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
};

export default useLessonActivities;
